<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'coachBillingExistedBills' }"
          marginBottom="5"/>

      <h6 class="element-header">
        {{ $t('external_coach_billing.title') }}
      </h6>

      <div class="element-box">
        <div class="row">
          <div class="col-12">
            <h5 class="form-header mb-4">
              Betreutes Wohnen - Erstellung von Abrechnungsinformationen
            </h5>
          </div>
        </div>

        <Search
            :filters="filters"
            :loading="isSearching"
            :coachDropdown="coachDropdown"
            :locationDropdown="locationDropdown"
            @search="getAssistedLivingCoachingPreviewList"/>

        <form @submit.prevent="" method="post">
          <div class="table-responsive overflow-auto pt-4">
            <table class="table table-striped">
              <thead>
              <tr>
                <th style="width: 3%;"></th>
                <th style="width: 25%;">Coach</th>
                <th style="text-align: right; width: 6%">Einzelpreis</th>
                <th style="text-align: right; width: 6%"># Sitzungen</th>
                <th style="text-align: right; width: 6%">Rechnungsbetrag</th>
                <th style="width: 5%;"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(coachBill) in coachBills.data" :key="coachBill.coachId">
                <td class="text-center">
                  <label class="custom-checkbox position-relative">
                    <input type="checkbox"
                           v-model="selectedCoachIds"
                           :value="coachBill.coachId"
                           class="custom-checkbox position-absolute"/>
                    <div class="custom-check-icon"></div>
                  </label>
                </td>
                <td>
                  {{ coachBill?.fullName }}
                </td>
                <td class="text-right">
                  {{ coachBill?.cost }}
                </td>
                <td class="text-right">
                  {{ coachBill?.unitsDone }}
                </td>
                <td class="text-right">
                  {{ coachBill?.total }}
                </td>
                <td class="text-right">
                  <PreviewButton :coachId="coachBill.coachId" :filters="filters"/>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div class="row" v-if="selectedCoachIds.length > 0">
            <div class="col-lg-6 col-md-6 col-sm-12">
              <div class="mt-5 text-right">
                <button type="submit" class="btn btn-primary" name="button_type" value="create_bill"
                        @click.prevent="createBill">
                  Rechnung erstellen
                </button>
              </div>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-12">
              <div class="mt-5 ml-1">
                <button type="submit" class="btn btn-primary" name="button_type" value="create_temp_bill"
                        @click.prevent="createTempBill">
                  Erstellen Sie ein temporäres Konto
                </button>
              </div>
            </div>
          </div>
          <div v-else class="text-center m-4" style="font-size: 16px; color:red">
            Wählen Sie einen Trainer aus, um eine Rechnung zu erstellen
          </div>
        </form>

      </div>
    </div>


    <ModalConfirm
        v-model="showCreateBillModal"
        :confirmationQuestion="t('general.approve_question')"
        header=""
        :isProcessing="isProcessing"
        :onConfirm="confirmCreateBill"/>

    <ModalConfirm
        v-model="showCreateTempBillModal"
        :confirmationQuestion="t('general.approve_question')"
        header=""
        :isProcessing="isProcessing"
        :onConfirm="confirmCreateTempBill"/>

  </PageTemplate>
</template>


<script setup>
import coachBillingService from '@/api/CoachBilling';
import BackButton from '@/components/button/BackButton.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {getExternalCoachForBillingDropdown, getOfficeDropdown} from '@/shared/dropdowns';
import PreviewButton from '@/views/App/CoachBilling/Accountant/Creating/AssistedLivingCoaching/PreviewButton.vue';
import Search from '@/views/App/CoachBilling/Accountant/Creating/AssistedLivingCoaching/Search.vue';
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();
const filters = ref({
  coachId: '',
  locationId: '',
  startDate: '',
  endDate: '',
});

const coachBills = ref([]);
const coachDropdown = ref([]);
const locationDropdown = ref([]);
const selectedCoachIds = ref([]);
const isSearching = ref(false);
const isLoaded = ref(false);
const showCreateBillModal = ref(false);
const isProcessing = ref(false);
const showCreateTempBillModal = ref(false);

async function getAssistedLivingCoachingPreviewList(passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  try {
    const {data} = await coachBillingService.assistedLivingCoachingPreviewList(
        passedFilters ? passedFilters : filters.value,
    );

    coachBills.value = [];
    coachBills.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function createBill() {
  CloseToast();
  showCreateBillModal.value = true;
  showCreateTempBillModal.value = false;
}



async function confirmCreateBill() {
  try {
    isProcessing.value = true

    filters.value.selectedCoachIds = selectedCoachIds.value;

    if (filters.value.coachId === '' || filters.value.coachId === undefined) {
      filters.value.coachId = null;
    }
    if (filters.value.locationId === '' || filters.value.locationId === undefined) {
      filters.value.locationId = null;
    }

    await coachBillingService.createAssistedLivingCoachingBill(filters.value);
    showCreateBillModal.value = false;
    isProcessing.value = false

    selectedCoachIds.value = [];
    await router.push({name: 'coachBillingExistedBills'});

    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
    isProcessing.value = false
    isSearching.value = false;
  }
}


function createTempBill() {
  CloseToast();
  showCreateTempBillModal.value = true;
  showCreateBillModal.value = false;
}

async function confirmCreateTempBill() {
  try {
    showCreateBillModal.value = false;
    showCreateTempBillModal.value = false;

    filters.value.selectedCoachIds = selectedCoachIds.value

    if (filters.value.coachId === "" || filters.value.coachId === undefined) {
      filters.value.coachId = null;
    }
    if (filters.value.locationId === "" || filters.value.locationId === undefined) {
      filters.value.locationId = null;
    }

    await coachBillingService.createAssistedLivingCoachingTempBill(filters.value);

    selectedCoachIds.value = [];
    await router.push({name: 'coachBillingPendingBills'});

    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}



onMounted(async () => {
  await Promise.all([
    getExternalCoachForBillingDropdown(coachDropdown),
    getOfficeDropdown(locationDropdown),
  ]);

  CloseToast();
  isLoaded.value = true;
});

</script>

<style scoped>

</style>